import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  AffiliateApplication,
  AffiliateApplicationPagination,
  GetAffiliateApplicationsParams,
} from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformGetAffiliateApplicationsResponse } from '../infra/mappers';

interface UseAffiliateApplicationsParams extends GetAffiliateApplicationsParams {
  projectId: string;
}

interface UseAffiliateApplicationsResult {
  applications: AffiliateApplication[];
  pagination: AffiliateApplicationPagination;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  isRefetching: boolean;
}

export const useAffiliateApplications = ({
  projectId,
  status,
  page = 1,
  page_size = 25,
  sort_by,
  sort_order,
  search,
}: UseAffiliateApplicationsParams): UseAffiliateApplicationsResult => {
  const {
    data: response,
    isError,
    isLoading,
    error,
    refetch,
    isRefetching,
  }: UseQueryResult<
    { applications: AffiliateApplication[]; pagination: AffiliateApplicationPagination },
    Error
  > = useQuery({
    queryKey: ['affiliate-applications', projectId, status, page, page_size, sort_by, sort_order, search],
    queryFn: async () => {
      const data = await affiliateApplicationService.getApplications(projectId, {
        status,
        page,
        page_size,
        sort_by,
        sort_order,
        search,
      });
      return transformGetAffiliateApplicationsResponse(data);
    },
    enabled: !!projectId,
  });

  const applications = useMemo(() => response?.applications ?? [], [response]);
  const pagination = useMemo(
    () =>
      response?.pagination ?? {
        page: 1,
        limit: page_size,
        total: 0,
        totalPages: 0,
      },
    [response, page_size],
  );

  return {
    applications,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  };
};
